import { FC, FunctionComponent, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import cs from "classnames";
import { ReactComponent as ChevronDownIcon } from "assets/icons/chevron-down.svg";
import styles from "../styles.module.scss";

interface IMenuItemBase {
  icon?: FunctionComponent;
  label: string;
  link?: string;
}

export interface IProps extends IMenuItemBase {
  index: number;
  isSidebarOpen: boolean;
  onCloseSubMenu: () => void;
  onCloseSidebar?: () => void;
  onOpenSubMenu?: (index: number) => void;
  openSubMenu: number | null;
  subMenu?: IMenuItemBase[];
  isMobileView?: boolean;
}

const MenuItem: FC<IProps> = ({
  icon: Icon,
  index,
  isSidebarOpen,
  label,
  link,
  onCloseSidebar,
  onCloseSubMenu,
  onOpenSubMenu,
  openSubMenu,
  subMenu,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const onClickRoute = useCallback(
    (routeLink: string) => {
      if (routeLink) {
        navigate(routeLink);
        if (onCloseSidebar) {
          onCloseSidebar();
        }
        onCloseSubMenu();
      }
    },
    [navigate, onCloseSidebar, onCloseSubMenu]
  );

  const isSubMenuItemActive = useCallback(
    (itemLink: string) => {
      const [linkWithoutHash] = itemLink.split("#");
      const [linkPath, linkSearch] = linkWithoutHash.split("?");
      if (linkPath !== location.pathname) return false;
      if (!linkSearch) return true;
      const currentParams = new URLSearchParams(location.search);
      return linkSearch.split("&").every((pair) => {
        const eqIdx = pair.indexOf("=");
        const key = pair.slice(0, eqIdx);
        const value = pair.slice(eqIdx + 1);
        return currentParams.get(key) === value;
      });
    },
    [location.pathname, location.search]
  );

  const isActive = useMemo(() => {
    if (subMenu) {
      return subMenu.some(({ link }) =>
        link ? link.split("?")[0] === location.pathname : false
      );
    }
    return link ? link === location.pathname : false;
  }, [link, location.pathname, subMenu]);

  const isOpenSubMenu = useMemo(
    () => !!(subMenu?.length && (index === openSubMenu || isActive)),
    [index, openSubMenu, subMenu?.length, isActive]
  );

  const isSubMenuAndSidebarOpen = useMemo(
    () => (isOpenSubMenu && isSidebarOpen) || false,
    [isOpenSubMenu, isSidebarOpen]
  );

  return (
    <li className={styles.menuItem}>
      <button
        className={cs({
          [styles.isActive]: isActive || isSubMenuAndSidebarOpen,
        })}
        onClick={() =>
          link && !subMenu ? onClickRoute(link) : onOpenSubMenu ? onOpenSubMenu(index) : undefined
        }
      >
        {Icon && <Icon />}
        <p>{label}</p>

        {isSidebarOpen && subMenu && (
          <ChevronDownIcon
            className={cs(styles.hasSubMenuIcon, {
              [styles.isExpanded]: isSubMenuAndSidebarOpen,
            })}
          />
        )}
      </button>
      {subMenu?.length ? (
        <ul
          className={cs(styles.subMenu, {
            [styles.isExpanded]: isSubMenuAndSidebarOpen,
          })}
        >
          {subMenu.map(({ label, link }) =>
            link ? (
              <li
                key={link}
                className={cs(styles.subMenuItem, {
                  [styles.isActiveSubMenuItem]: isSubMenuItemActive(link),
                })}
                onClick={() => onClickRoute(link)}
              >
                {label}
              </li>
            ) : null
          )}
        </ul>
      ) : null}
    </li>
  );
};

export default MenuItem;
